import { call, CallEffect, put, PutEffect, select, SelectEffect, debounce, takeLatest } from 'redux-saga/effects';

import { AUTH_SUCCESS } from 'js/actionCreators/authActions';
import { showError } from 'js/actionCreators/uiActions';
import { APP_DATA_KEYS } from 'js/config/consts';
import {
  setLastSynced,
  setUseBrandKitFonts,
  addStyle,
  removeStyle,
  updateStyle,
  setDefaultStyle,
  loadFontsSuccess,
  TextStyle
} from 'js/reducers/brandKitFonts';
import { appServices } from 'js/shared/helpers/app-services/AppServices';
import { HTTPError, RootState } from 'js/types';

type BrandKitFontsPayload = { styles: TextStyle[]; useBrandKitFonts: boolean };

export const getBrandKitFonts = (state: RootState) => ({
  styles: state.brandKitFonts.styles,
  useBrandKitFonts: state.brandKitFonts.useBrandKitFonts
});

export function* syncBrandKitFonts(): Generator<SelectEffect | CallEffect | PutEffect, void, BrandKitFontsPayload> {
  try {
    const payload = yield select(getBrandKitFonts);
    yield call({ fn: appServices.setAppData, context: appServices }, APP_DATA_KEYS.BRAND_KIT_FONTS, payload);
    yield put(setLastSynced(Date.now()));
  } catch (error) {
    yield put(
      showError({
        message: 'Error syncing brand kit fonts. Please try again later.',
        description: error instanceof Error ? error.message : undefined
      })
    );
    console.error('Error syncing brand kit fonts:', error);
  }
}

export function* loadBrandKitFonts(): Generator<CallEffect | PutEffect, void, BrandKitFontsPayload> {
  try {
    const payload = yield call({ fn: appServices.getAppData, context: appServices }, APP_DATA_KEYS.BRAND_KIT_FONTS);
    yield put(loadFontsSuccess(payload));
  } catch (error) {
    const httpError = error as HTTPError;
    const statusCode = httpError?.httpStatusCode;

    // 404 is expected when the user hasn't saved any font settings yet
    if (statusCode === 404 || statusCode === '404') {
      console.log('Brand kit fonts not found (404) - using defaults');
      return;
    }

    console.error('Error loading brand kit fonts:', error);
    yield put(
      showError({
        message: 'Error loading brand kit fonts. Please try again later.',
        description: error instanceof Error ? String(error.message) : String(error)
      })
    );
  }
}

export const FONTS_SYNC_DELAY = 2000;
export default function* brandKitFontsSaga() {
  yield debounce(
    FONTS_SYNC_DELAY,
    [setUseBrandKitFonts.type, addStyle.type, removeStyle.type, updateStyle.type, setDefaultStyle.type],
    syncBrandKitFonts
  );
  yield takeLatest(AUTH_SUCCESS, loadBrandKitFonts);
}
